/* App.jsx — split protected vs token-only company paths */
import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";

import SignInPage from "./components/SignInPage";
import HomePage from "./components/HomePage";
import CompanyHome from "./components/CompanyHome";
import JobForm from "./components/JobTypeForm";
import Admin from "./components/admin";
import Layout from "./components/Layout";
import Charts from "./components/Charts";
import {
  Details,
  UserProvider,
  ThemeProvider,
  RequireCreds,
  useUser,
} from "ogcommon";

import "./index.css";
import { serverValidateToken, softValidateToken } from "./utils/tokenUtils";

/** Gate that accepts EITHER a ?token=… OR a signed-in session */
function TokenOrCredsGate({ children }) {
  const loc = useLocation();
  const qs = new URLSearchParams(loc.search);
  const token = qs.get("token");
  const { userID } = useUser();

  if (token) {
    // Soft client-side validation (exp/nbf/aud)
    const v = softValidateToken(token, { audience: "house.ogendeavors.com", leeway: 60 });
    if (!v.ok) {
      const to = `/sign-in?reason=${encodeURIComponent(v.reason)}&redirect=${encodeURIComponent(
        loc.pathname + loc.search
      )}`;
      return <Navigate to={to} replace />;
    }

    // (Optional) Server validation to confirm signature:
    // (Uncomment if you add a HEAD validator endpoint that returns 200 on success)
    // const sv = await serverValidateToken(token, "https://house.ogendeavors.com/api/validate");
    // if (!sv.ok) {
    //   const to = `/sign-in?reason=${encodeURIComponent(sv.reason)}&redirect=${encodeURIComponent(
    //     loc.pathname + loc.search
    //   )}`;
    //   return <Navigate to={to} replace />;
    // }

    // Persist token for API calls
    sessionStorage.setItem("og_token", token);
    return children;
  }

  // No token → require normal signed-in session
  if (userID) return children;

  // Not signed in and no token → go sign in
  const to = `/sign-in?redirect=${encodeURIComponent(loc.pathname + loc.search)}`;
  return <Navigate to={to} replace />;
}

export default function App() {
  return (
    <UserProvider>
      <ThemeProvider>
          <Routes>
            {/* Public (no creds) */}
            <Route path="/" element={<SignInPage />} />
            <Route path="/sign-in" element={<SignInPage />} />
            <Route path="/Charts" element={<Charts />} />

            {/* Single route for companyHome: token OR creds allowed */}
            <Route
              path="/companyHome/:subdomain"
              element={
                <TokenOrCredsGate>
                  <Layout>
                    <CompanyHome />
                  </Layout>
                </TokenOrCredsGate>
              }
            />

            {/* PROTECTED routes (session creds) */}
            <Route element={<RequireCreds />}>
              <Route
                path="/home"
                element={
                  <Layout>
                    <HomePage />
                  </Layout>
                }
              />
              <Route
                path="/job-form"
                element={
                  <Layout>
                    <JobForm />
                  </Layout>
                }
              />
              <Route
                path="/profile-details"
                element={
                  <Layout>
                    <Details />
                  </Layout>
                }
              />
              <Route
                path="/admin-panel"
                element={
                  <Layout>
                    <Admin />
                  </Layout>
                }
              />
            </Route>

            {/* Catch-all */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
      </ThemeProvider>
    </UserProvider>
  );
}
